import { useState, useEffect, useMemo } from 'react';
import { fetchWrapper } from 'utils/apiFetch';

export const useTableData = (apiUrl, index=0, config = {}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(config.defaultItemsPerPage || 10);
  const [sortField, setSortField] = useState(config.defaultSortField || 'created_at');
  const [sortDirection, setSortDirection] = useState(config.defaultSortDirection || 'desc');
  const [selectedRows, setSelectedRows] = useState([]);
  const [filters, setFilters] = useState(config.defaultFilters || {});

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await fetchWrapper(apiUrl);
  
     
  
      const result = response;
  
      // --- Debug: see raw payload ---
      console.log("Raw API response:", result);
  
      // --- Normalize data array ---
      let fetched = [];
  
      // Case 1: normal
      if (Array.isArray(result.data)) {
        fetched = result.data;
      }
      // Case 2: wrapped object
      else if (result.data?.data && Array.isArray(result.data.data)) {
        fetched = result.data.data;
      }
      // Case 3: com_data variant
      else if (Array.isArray(result.com_data)) {
        fetched = result.com_data;
      }
      // Case 4: com_data nested
      else if (result.com_data?.data && Array.isArray(result.com_data.data)) {
        fetched = result.com_data.data;
      }
      else {
        console.warn("⚠️ Unknown response shape:", result);
      }
  
      // --- Debug: check structure ---
      if (fetched.length > 0) {
        console.log("✅ Parsed first item keys:", Object.keys(fetched[0]));
      } else {
        console.log("⚠️ No data items parsed:", fetched);
      }
  
      setData(fetched);
    } catch (err) {
      console.error("Error fetching data:", err);
      setError(`Failed to fetch data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  

  

  useEffect(() => {
    fetchData();
  }, [apiUrl]);

  // Filter functionality
  const filteredData = useMemo(() => {
    if (!Array.isArray(data)) return [];
  
    return data.filter(item => {
      const matchesSearch = Object.values(item).some(value =>
        value && value.toString().toLowerCase().includes(searchTerm.toLowerCase())
      );
  
      const matchesFilters = Object.entries(filters).every(([key, value]) => {
        if (value === 'all' || !value) return true;
        return item[key] === value;
      });
      
      return matchesSearch && matchesFilters;
    });
  }, [data, searchTerm, filters]);
  

  // Sort functionality
  const sortedData = useMemo(() => {
  return [...filteredData].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];

    // Handle date sorting
    if (sortField === "created_at") {
      aValue = new Date(aValue);
      bValue = new Date(bValue);
    }

    // Handle numeric fields
    else if (config.numericFields?.includes(sortField)) {
      aValue = parseFloat(aValue || 0);
      bValue = parseFloat(bValue || 0);
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;

    return 0;
  });
}, [filteredData, sortField, sortDirection, config.numericFields]);

  // Pagination
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedData.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedData, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(sortedData.length / itemsPerPage);

  // Handle sorting
  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Handle row selection
  const handleRowSelect = (id) => {
    setSelectedRows(prev =>
      prev.includes(id)
        ? prev.filter(rowId => rowId !== id)
        : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedRows.length === paginatedData.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(paginatedData.map(item => item.id));
    }
  };

  // Handle filter changes
  const handleFilterChange = (filterKey, value) => {
    setFilters(prev => ({ ...prev, [filterKey]: value }));
    setCurrentPage(1);
  };

  return {
    data,
    loading,
    error,
    searchTerm,
    setSearchTerm,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    sortField,
    sortDirection,
    handleSort,
    selectedRows,
    handleRowSelect,
    handleSelectAll,
    filters,
    handleFilterChange,
    paginatedData,
    sortedData,
    totalPages,
    refetch: fetchData
  };
};